import isEqual from 'react-fast-compare'
import { BehaviorSubject, firstValueFrom, from, merge, Observable, OperatorFunction, Subject, Subscription } from 'rxjs'
import { buffer, debounceTime, filter, map, skip, switchMap } from 'rxjs/operators'
import { getRandomId } from './utilities'
import cloneDeep from 'lodash-es/cloneDeep'

let subscriptions: Subscription[] = []

export function addSubscriptions(...subscription: Subscription[]) {
	subscriptions.push(...subscription)
}

export function unsubscribeAll() {
	for (const subscription of subscriptions) {
		subscription.unsubscribe()
	}
	subscriptions = []
}

export class StackBehaviourSubject<T> extends BehaviorSubject<T> {
	skipInitial: Observable<T>

	constructor(value: T) {
		super(value)
		this.skipInitial = this.pipe(skip(1))
	}

	nextIfDifferent(value: T) {
		if (!isEqual(this.value, value)) {
			this.next(value)
		}
	}

	mutate(mutator: (value: T) => T) {
		this.nextIfDifferent(mutator(cloneDeep(this.value)))
	}
}

export function waitForObservable<T>(subject: Observable<T>) {
	return new Promise<T>((resolve, reject) => {
		let subscription: Subscription | undefined = undefined
		subscription = subject.subscribe(
			value => {
				resolve(value)
				subscription?.unsubscribe()
			},
			error => reject(error)
		)
	})
}

export function waitUntilTrue(subject: Observable<boolean>) {
	return waitForObservable(subject.pipe(filter(value => value)))
}

type QueueRequest<T> = { id: string; makeRequest: () => Promise<T> }
type QueueResult<T> = { id: string } & ({ successful: true; result: T } | { successful: false; error?: any })
export class OrderedSequentialQueue<T> {
	private requestQueue: QueueRequest<T>[] = []
	private requestComplete = new BehaviorSubject<QueueResult<T> | undefined>(undefined)
	private currentRequest = new BehaviorSubject<QueueRequest<T> | undefined>(undefined)

	constructor() {
		this.currentRequest.subscribe(async request => {
			if (!request) {
				return
			}
			try {
				const result = await request.makeRequest()
				this.requestComplete.next({ successful: true, id: request.id, result })
			} catch (e: any) {
				this.requestComplete.next({ successful: false, id: request.id, error: e })
			}
			this.currentRequest.next(this.requestQueue.shift())
		})
	}

	async waitForQueue() {
		await waitUntilTrue(
			this.currentRequest.pipe(
				filter(x => !x),
				map(x => !x)
			)
		)
	}

	async addToQueue(action: () => Promise<T>) {
		const id = getRandomId()
		const request = { id, makeRequest: action }
		if (this.currentRequest.value) {
			this.requestQueue.push(request)
		} else {
			this.currentRequest.next(request)
		}

		return new Promise<T>(async (resolve, reject) => {
			let subscription: Subscription | undefined = undefined
			subscription = this.requestComplete.subscribe({
				next: x => {
					if (x?.id !== id) {
						return
					}
					if (x.successful) {
						resolve(x.result)
					} else {
						reject(x.error)
					}
					subscription?.unsubscribe()
				},
				error: reject
			})
		})
	}
}

export class BatchQueue<T, TResult> {
	private readonly DEBOUNCE_TIME: number
	private readonly changeSubject = new Subject<T>()
	private readonly processChanges: (changes: T[]) => Promise<TResult>
	private readonly processingSubject = new Subject<boolean>()
	private readonly resultSubject = new Subject<{ result: TResult; changes: T[] }>()
	constructor(debounceMs: number, processChanges: (changes: T[]) => Promise<TResult>) {
		this.DEBOUNCE_TIME = debounceMs
		this.processChanges = processChanges

		// Create a stream that emits true when processing starts and false when it ends
		const processingStream = this.processingSubject.pipe(
			switchMap(isProcessing => {
				return from([isProcessing])
			})
		)

		// Buffer changes while processing is happening or within debounce window
		const changes$ = this.changeSubject.pipe(
			buffer(
				merge(
					this.changeSubject.pipe(debounceTime(this.DEBOUNCE_TIME) as OperatorFunction<T, T>),
					processingStream.pipe(
						filter(isProcessing => !isProcessing),
						debounceTime(this.DEBOUNCE_TIME) as OperatorFunction<boolean, boolean>
					)
				)
			),
			filter(changes => changes.length > 0)
		)

		// Process buffered changes
		changes$.subscribe(async changes => {
			this.processingSubject.next(true)
			try {
				const result = await this.processChanges(changes)
				this.resultSubject.next({ result, changes })
			} finally {
				this.processingSubject.next(false)
			}
		})
	}

	emit(change: T): Promise<TResult> {
		this.changeSubject.next(change)
		return firstValueFrom(
			this.resultSubject.pipe(
				filter(x => x.changes.includes(change)),
				map(x => x.result)
			)
		)
	}

	dispose(): void {
		this.changeSubject.complete()
		this.processingSubject.complete()
	}
}
